/**
 * Pure 4/8-direction feet-body movement with grid collision (owner: env-builder).
 * Used by the development player mover until the characters builder's PlayerController lands;
 * also handy for NPC stand-ins. No Phaser import.
 */
import type { Facing } from '@/core/types';

export interface MoveInput { dx: -1 | 0 | 1; dy: -1 | 0 | 1 }
/** Collision body at the feet: ARCHITECTURE §1 says 20 × 12 anchored at the feet baseline. */
export interface FeetBody { halfW: number; h: number }
export const DEFAULT_BODY: FeetBody = { halfW: 10, h: 12 };

export type BlockedFn = (px: number, py: number) => boolean;

/** True when any corner of the feet body at (x, y) touches a blocked cell. */
export function bodyBlocked(x: number, y: number, isBlocked: BlockedFn, body: FeetBody = DEFAULT_BODY): boolean {
  const l = x - body.halfW, r = x + body.halfW - 1, b = y - 1, t = y - body.h;
  return isBlocked(l, b) || isBlocked(r, b) || isBlocked(l, t) || isBlocked(r, t) || isBlocked(x, b) || isBlocked(x, t);
}

export interface MoveResult { x: number; y: number; moved: boolean; facing: Facing | null }

/**
 * Advances a floating-point position by `speed` px/s for `dtMs`, resolving each axis separately so
 * the body slides along walls. Returns the new (unrounded) position and the facing implied by input.
 */
export function stepMove(x: number, y: number, input: MoveInput, speedPxPerSec: number, dtMs: number, isBlocked: BlockedFn, body: FeetBody = DEFAULT_BODY): MoveResult {
  const { dx, dy } = input;
  if (dx === 0 && dy === 0) return { x, y, moved: false, facing: null };
  const facing: Facing = dx !== 0 && dy === 0 ? (dx > 0 ? 'right' : 'left') : dy !== 0 && dx === 0 ? (dy > 0 ? 'down' : 'up') : (dx > 0 ? 'right' : 'left');
  const dist = (speedPxPerSec * dtMs) / 1000;
  const norm = dx !== 0 && dy !== 0 ? Math.SQRT1_2 : 1;
  let nx = x, ny = y;
  const tryX = x + dx * dist * norm;
  if (dx !== 0 && !bodyBlocked(Math.round(tryX), Math.round(ny), isBlocked, body)) nx = tryX;
  const tryY = ny + dy * dist * norm;
  if (dy !== 0 && !bodyBlocked(Math.round(nx), Math.round(tryY), isBlocked, body)) ny = tryY;
  const moved = Math.round(nx) !== Math.round(x) || Math.round(ny) !== Math.round(y);
  return { x: nx, y: ny, moved, facing };
}

/** Facing from a direction vector (dominant axis; ties prefer horizontal). */
export function facingFrom(dx: number, dy: number, fallback: Facing = 'down'): Facing {
  if (dx === 0 && dy === 0) return fallback;
  return Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up');
}
